import { Filter } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  CoverageCategory,
  CoverageSchema,
  MentionStatus,
} from '@/services/types/coverage-types';

interface MentionsFiltersProps {
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (value: string) => void;
  artifactTypeFilter: string;
  onArtifactTypeFilterChange: (value: string) => void;
  validOnlyFilter: boolean;
  onValidOnlyFilterChange: (value: boolean) => void;
  coverageFilter: string;
  onCoverageFilterChange: (value: string) => void;
  availableCoverages: CoverageSchema[];
}

const getCoverageDisplayName = (coverage: CoverageSchema): string => {
  // Start with carrier name
  let name = coverage.carrier?.name || 'Unknown Carrier';

  // Add first insured name if available
  if (coverage.named_insured && coverage.named_insured.length > 0) {
    const firstName = coverage.named_insured[0].split(' ')[0];
    name += ` • ${firstName}`;
  }

  // Add primary policy identifier if available
  if (coverage.policy_identifiers && coverage.policy_identifiers.length > 0) {
    const primaryId = coverage.policy_identifiers[0];
    name += ` • [${primaryId.identifier_value.slice(0, 12)}${primaryId.identifier_value.length > 12 ? '...' : ''}]`;
  }

  return name;
};

export const MentionsFilters = ({
  statusFilter,
  onStatusFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  artifactTypeFilter,
  onArtifactTypeFilterChange,
  validOnlyFilter,
  onValidOnlyFilterChange,
  coverageFilter,
  onCoverageFilterChange,
  availableCoverages,
}: MentionsFiltersProps) => {
  const hasActiveFilters =
    statusFilter !== 'all' ||
    categoryFilter !== 'all' ||
    artifactTypeFilter !== 'all' ||
    coverageFilter !== 'all' ||
    validOnlyFilter;

  const clearAllFilters = () => {
    onStatusFilterChange('all');
    onCategoryFilterChange('all');
    onArtifactTypeFilterChange('all');
    onCoverageFilterChange('all');
    onValidOnlyFilterChange(false);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Primary filters - most important for audit */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Coverage Filter - Primary for audit */}
        <Select value={coverageFilter} onValueChange={onCoverageFilterChange}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Filter by linked coverage..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Coverage States</SelectItem>
            <SelectItem value="unlinked">🔗 Unlinked Mentions</SelectItem>
            <SelectItem value="linked">✅ Linked Mentions</SelectItem>
            {availableCoverages.length > 0 && <div className="border-t my-1" />}
            {availableCoverages.map((coverage) => (
              <SelectItem key={coverage.id} value={coverage.id}>
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {getCoverageDisplayName(coverage)}
                  </span>
                  {coverage.category && (
                    <span className="text-xs text-gray-500">
                      {coverage.category.charAt(0)}P
                    </span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={onStatusFilterChange}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value={MentionStatus.PENDING}>Pending</SelectItem>
            <SelectItem value={MentionStatus.READY}>Ready</SelectItem>
            <SelectItem value={MentionStatus.ACCEPTED}>Accepted</SelectItem>
            <SelectItem value={MentionStatus.REJECTED}>Rejected</SelectItem>
          </SelectContent>
        </Select>

        {/* Valid Only Switch */}
        <div className="flex items-center space-x-2">
          <Switch
            id="valid-only"
            checked={validOnlyFilter}
            onCheckedChange={onValidOnlyFilterChange}
          />
          <Label htmlFor="valid-only" className="text-sm">
            Valid only
          </Label>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllFilters}
            className="ml-auto"
          >
            <Filter className="h-4 w-4 mr-1" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Secondary filters - collapsible or less prominent */}
      <div className="flex gap-3 items-center text-sm">
        <Select value={categoryFilter} onValueChange={onCategoryFilterChange}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value={CoverageCategory.FIRST_PARTY}>
              1st Party
            </SelectItem>
            <SelectItem value={CoverageCategory.FIRST_PARTY_HEALTH}>
              1st Health
            </SelectItem>
            <SelectItem value={CoverageCategory.THIRD_PARTY}>
              3rd Party
            </SelectItem>
            <SelectItem value={CoverageCategory.NOT_LIABLE_INVOLVED_PARTY}>
              Not-Liable
            </SelectItem>
            <SelectItem value={CoverageCategory.UNCLEAR}>Unclear</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={artifactTypeFilter}
          onValueChange={onArtifactTypeFilterChange}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            <SelectItem value="DOCUMENT">Document</SelectItem>
            <SelectItem value="EMAIL">Email</SelectItem>
            <SelectItem value="IMAGE">Image</SelectItem>
            <SelectItem value="AUDIO">Audio</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
